import {
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Checkbox,
  HStack,
} from "@chakra-ui/react";
import { EditIcon } from "@chakra-ui/icons";
import { FormEvent, useEffect, useRef, useState } from "react";
import { Button } from "../Button";
import { useModal } from "../../hooks/useModal";
import { colors } from "../../colors";
import { Show } from "../Show";
import { styled } from "styled-components";
import icon from "../../assets/skipgive-icon.png";
import { Image } from "../Reusable";
import { useShowToast } from "../../hooks/useShowToast";
import { useAuth } from "../../hooks/useAuth";
import {
  CreateClientResponse,
  postCreateClient,
} from "../../api/clientUser/postCreateClient";
import { Session } from "@supabase/supabase-js";
import { EinSearchBar } from "../EINSearchBar";
import { EINData } from "../../api/clientUser/getEinLookup";
import {
  CreateUserResponse,
  postCreateUser,
} from "../../api/user/postCreateUser";

const WelcomeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 30rem;
`;

const TermsContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Link = styled.a`
  color: ${colors.gold};
`;

export const CreateAccountModal = () => {
  const { signUp, setStripeConnected } = useAuth();
  const { closeModal, activeModal } = useModal();
  const [email, setEmail] = useState("");
  const [orgOrIndividualName, setOrgOrIndividualName] = useState("");
  const [password, setPassword] = useState("");
  const [ein, setEin] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const { showToast } = useShowToast();
  const [termsSelected, setTermsSelected] = useState(false);
  const initialRef = useRef(null);
  const finalRef = useRef(null);

  useEffect(() => {
    return () => setEin("");
  }, []);

  if (activeModal !== "create-organization" && activeModal !== "create-individual") return null;

  localStorage.setItem("onboarding", "true");

  const handleCreateAccount = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setIsProcessing(true);

      if (!orgOrIndividualName || !email || !password) {
        showToast("You must enter all fields!", "warning");
        return;
      }

      if (confirmPassword !== password) {
        showToast("The passwords you entered do not match!", "warning");
        return;
      }

      const { data, error } = await signUp(email, password);

      if (error) {
        if (error.message === "Client already registered") {
          showToast("This client already exists! Sign in instead.", "error");
        } else {
          showToast("There was an error completing the request!", "error");
        }
        return;
      }

      const sessionData: Session = data.session;

      // If session is null, email confirmation is required
      if (!sessionData) {
        // Store signup info for after email confirmation
        localStorage.setItem(
          "pendingSignup",
          JSON.stringify({
            email,
            orgOrIndividualName,
            ein,
            accountType: activeModal,
          })
        );
        setShowEmailConfirmation(true);
        return;
      }

      if (sessionData?.access_token) {
        let res: CreateClientResponse | CreateUserResponse | undefined;
        if (activeModal === "create-organization" || activeModal === "create-individual") {
          res = await postCreateClient(
            sessionData.user.id,
            email,
            orgOrIndividualName,
            ein,
            sessionData.access_token
          );
        } else {
          res = await postCreateUser(
            sessionData.user.id,
            email,
            orgOrIndividualName,
            sessionData.access_token
          );
        }

        if (!res?.id) {
          showToast("Failed to create user!", "error");
        } else {
          showToast("Your account has been created 💪", "success");
          setShowEmailConfirmation(true);
          setStripeConnected(!!res.id);
        }
      } else {
        showToast("An unknown error has occurred!", "error");
      }
    } catch (error) {
      showToast("There was an error completing the request!", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSearchSelectResult = (result: EINData) => {
    setOrgOrIndividualName(result.name);
    setEin(result.ein);
  };

  const einSelect = (
    <FormControl>
      <FormLabel>First, let's find your non-profit.</FormLabel>
      <EinSearchBar onSelectResult={handleSearchSelectResult} />
    </FormControl>
  );

  const form = (
    <form onSubmit={handleCreateAccount} style={{ padding: "1rem" }}>
      <ModalHeader fontSize={"2xl"}>
        <HStack spacing={2}>
          <EditIcon color={colors.gold} />
          <Text>{`Register ${
            activeModal === "create-organization" ? "Organization" : "Individual"
          }`}</Text>
        </HStack>
      </ModalHeader>
      <ModalCloseButton />
      <ModalBody pb={6} minHeight={"6rem"}>
        {activeModal === "create-individual" && (
          <Text fontSize="sm" color="gray.600" mb={4}>
            As an individual fundraiser, you can raise money on behalf of
            registered 501(c)(3) nonprofit organizations. You'll select the
            nonprofit you're supporting when you create a campaign.
          </Text>
        )}
        <Show
          when={!!ein || activeModal === "create-individual"}
          whenFalseContent={einSelect}
        >
          <FormControl>
            <FormLabel>
              {activeModal === "create-organization"
                ? "Organization Name"
                : "Your Name"}
            </FormLabel>
            <Input
              placeholder={
                activeModal === "create-organization"
                  ? orgOrIndividualName
                  : "John Doe"
              }
              focusBorderColor={colors.gold + "60"}
              onChange={(e) => setOrgOrIndividualName(e.target.value)}
              type="text"
              disabled={activeModal === "create-organization"}
            />
          </FormControl>
          <FormControl mt={4}>
            <FormLabel>Email</FormLabel>
            <Input
              placeholder="mary@example.com"
              focusBorderColor={colors.gold + "60"}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
            />
          </FormControl>
          <FormControl mt={4}>
            <FormLabel>Password</FormLabel>
            <Input
              placeholder="Password"
              focusBorderColor={colors.gold + "60"}
              onChange={(e) => setPassword(e.target.value)}
              type="password"
            />
          </FormControl>
          <FormControl mt={4}>
            <FormLabel>Re-Enter Password</FormLabel>
            <Input
              placeholder="Password"
              focusBorderColor={colors.gold + "60"}
              onChange={(e) => setConfirmPassword(e.target.value)}
              type="password"
            />
          </FormControl>
          <FormControl mt={4}>
            <TermsContainer>
              <Checkbox
                marginRight={"1rem"}
                isChecked={termsSelected}
                onChange={() => setTermsSelected(!termsSelected)}
              />
              <Text>
                By creating an account you're agreeing to SkipGive's{" "}
                <Link href={"/tos"} target="_blank">
                  terms of service
                </Link>{" "}
                and our{" "}
                <Link href={"/sms-policy"} target="_blank">
                  SMS text policy
                </Link>
                .
              </Text>
            </TermsContainer>
          </FormControl>
        </Show>
      </ModalBody>
      <Show when={!!ein || activeModal === "create-individual"}>
        <ModalFooter>
          <Button
            onClick={() => {
              setEin("");
              closeModal();
            }}
            buttonType="secondary"
            label="Cancel"
            type="button"
          />
          <Button
            buttonType="primary"
            label="Create"
            isLoading={isProcessing}
            type="submit"
            disabled={!termsSelected}
          />
        </ModalFooter>
      </Show>
    </form>
  );

  const emailConfirmationDialog = (
    <>
      <WelcomeWrapper>
        <Image src={icon} />
        <ModalHeader margin={"-3"} fontSize={"3xl"}>
          Thanks for Signing Up!
        </ModalHeader>
        <Text textAlign="center" paddingX="2rem" color="#4a5568">
          We've sent a confirmation email to <strong>{email}</strong>. Please
          check your inbox and click the confirmation link to complete your
          registration.
        </Text>
        <div style={{ marginTop: "2rem" }}>
          <Button
            onClick={() => {
              setShowEmailConfirmation(false);
              closeModal();
            }}
            buttonType="primary"
            label="Got It"
            type="button"
          />
        </div>
      </WelcomeWrapper>
    </>
  );

  return (
    <Modal
      initialFocusRef={initialRef}
      finalFocusRef={finalRef}
      isOpen={true}
      onClose={() => {
        setEin("");
        closeModal();
      }}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent margin="auto">
        <Show when={showEmailConfirmation} whenFalseContent={form}>
          {emailConfirmationDialog}
        </Show>
      </ModalContent>
    </Modal>
  );
};
